<script lang="ts" setup>
import useSwr from "@/core/composables/swr/useSwr";
import {computed, reactive, ref, watch} from "vue";
import {filterEmptyKeys} from "@/lib/utils";
import {useRoute, useRouter} from "vue-router";
import {loyaltyCardApi} from "@/container";
import {LOYALTY_CARD_FILL_COLORS} from "@/loyalty-card/constants";
import useLoyaltyCardAnalysis from "@/loyalty-card/composables/useLoyaltyCardAnalysis";
import LoyaltyCardService, {LOYALTY_CARD_FILL_QUARTILE_IDS} from "@/loyalty-card/services/LoyaltyCardService";
import {OFFER_DISTANCE} from "@/loyalty-card-membership/services/membershipProgress";
import Icon from "@/core/components/icon/Icon.vue";
import {LoyaltyCardMembershipHistoryDialog} from "@/loyalty-card-membership/components";
import type {
    LoyaltyCardMembershipHistorySummary
} from "@/loyalty-card-membership/components/loyalty-card-membership-history-dialog/LoyaltyCardMembershipHistoryDialog.vue";

const router = useRouter();
const route = useRoute();
const props = defineProps<{
    resourceId: number;
}>();

// Shares the dashboard's SWR key: no extra request in practice.
const {data: loyaltyCard} = useLoyaltyCardAnalysis(props.resourceId);
const {resolveCardModel, resolveFillThreshold, fillQuartiles, objectiveUnitLabel} = LoyaltyCardService();

const ALL_CHIP = {id: "all", label: "Toutes", color: null};
const NEAR_THRESHOLD_CHIP = {id: "near_threshold", label: "Proches du seuil", color: "#ffbc68"};

/** Every `fill` value the listing accepts, whatever the card threshold. */
const acceptedFills: string[] = [ALL_CHIP.id, ...LOYALTY_CARD_FILL_QUARTILE_IDS, NEAR_THRESHOLD_CHIP.id];

/**
 * Fill filter chips. The quartile chips are derived from the card threshold
 * (the same split the API applies), so their bounds are only shown once the
 * card is loaded; "x à y" rather than "x-y" so the dash is not read as a digit.
 */
const fillChips = computed(() => {
    const quartiles = loyaltyCard.value
        ? fillQuartiles(resolveFillThreshold(loyaltyCard.value)).map((quartile, index) => ({
            id: quartile.id,
            label: quartile.min === quartile.max ? `${quartile.min}` : `${quartile.min} à ${quartile.max}`,
            color: LOYALTY_CARD_FILL_COLORS[index]
        }))
        : [];

    return [ALL_CHIP, ...quartiles, NEAR_THRESHOLD_CHIP];
});

/** Initial fill filter, preselectable through the URL (dashboard KPI link). */
const initialFill = acceptedFills.find((fill) => fill === route.query.fill) ?? ALL_CHIP.id;

const searchParams = reactive<{ page: number; itemsPerPage: number; fill: string }>({
    page: 1,
    itemsPerPage: 20,
    fill: initialFill
});

/**
 * The computed SWR key.
 */
const swrKey = computed(() => {
    const query = new URLSearchParams(filterEmptyKeys(searchParams));

    return `loyalty-card/${props.resourceId}/memberships?${query.toString()}`;
});

const {data: consumers, isLoading, isValidating} = useSwr(
    swrKey,
    () => loyaltyCardApi().getMemberships(props.resourceId, searchParams.page, searchParams.itemsPerPage, searchParams.fill)
);

/** Whether the balance column shows cumulated points instead of bought products. */
const isPointCard = computed(() => (loyaltyCard.value ? resolveCardModel(loyaltyCard.value) === "point" : false));

/**
 * Build initials from a customer name.
 */
const initials = (name?: string): string => {
    if (!name) {
        return "?";
    }

    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
};

/**
 * Fallback for backends that predate the served flag: the same band as the
 * offer button (a few stamps short of the threshold, not past it).
 */
const isNearThreshold = (current: number, threshold: number): boolean => (
    threshold - current > 0 && threshold - current <= OFFER_DISTANCE
);

const dataTableConsumers = computed(() => {
    if (!consumers.value) {
        return [];
    }

    return consumers.value.data.map((consumer) => ({
        customerId: consumer.customer?.id,
        membershipId: consumer.customer?.membership_id,
        name: consumer.customer?.name,
        initials: initials(consumer.customer?.name),
        createdAt: consumer.created_at ?? "—",
        products: isPointCard.value ? consumer.total.point_balance : consumer.total.quantity,
        frequency: consumer.statistics.frequency_days != null ? `${consumer.statistics.frequency_days} j` : "—",
        rewardsCount: consumer.statistics.rewards_count ?? 0,
        fillCurrent: consumer.fill?.current ?? 0,
        fillThreshold: consumer.fill?.threshold ?? 0,
        // Served by the API (single source of the rule); the arithmetic
        // fallback only covers backends that predate the flag.
        nearThreshold: consumer.fill?.near_threshold
            ?? isNearThreshold(consumer.fill?.current ?? 0, consumer.fill?.threshold ?? 0)
    }));
});

const setFill = (fill: string) => {
    searchParams.fill = fill;
    searchParams.page = 1;

    // Keep the URL in sync so a reload or back-navigation restores the
    // filter the user actually selected, not the one they arrived with.
    router.replace({query: {...route.query, fill: fill === ALL_CHIP.id ? undefined : fill}});
};

// A quartile preselected through the URL may not exist for this card (its
// floor exceeds the threshold): fall back to "Toutes" once the card is known
// rather than keeping a filter no chip accounts for.
watch(fillChips, (chips) => {
    if (loyaltyCard.value && !chips.some((chip) => chip.id === searchParams.fill)) {
        setFill(ALL_CHIP.id);
    }
});

/**
 * Fill bar width in percent.
 */
const fillPercent = (item: { fillCurrent: number; fillThreshold: number }): number => (
    item.fillThreshold ? Math.min(100, (item.fillCurrent / item.fillThreshold) * 100) : 0
);

/**
 * Fill bar color depending on how close the card is to its reward.
 */
const fillColor = (item: { fillCurrent: number; fillThreshold: number; nearThreshold: boolean }): string => {
    if (item.fillCurrent >= item.fillThreshold) {
        return "#419e83";
    }

    if (item.nearThreshold) {
        return "#ffbc68";
    }

    return "#3513e2";
};

/**
 * Handle item per page change.
 */
const handleItemPerPageChange = (itemsPerPage: number) => {
    searchParams.page = 1;
    searchParams.itemsPerPage = itemsPerPage;
};

/**
 * Handle page change.
 */
const handlePageChange = (page: number) => {
    searchParams.page = page;
};

const historyOpen = ref(false);
const historySummary = ref<LoyaltyCardMembershipHistorySummary | null>(null);
// Bumped on every opening: the dialog starts over, back on "Tous", even on the same card.
const historyOpening = ref(0);

/**
 * Open the card history over the listing, so going through the cards does not
 * lose the listing; the patient record stays one click away in the dialog.
 */
const handleRowClick = (_: unknown, row: { item: (typeof dataTableConsumers.value)[number] }) => {
    const item = row.item;

    if (!item.customerId || !item.membershipId) {
        return;
    }

    historySummary.value = {
        customerId: item.customerId,
        membershipId: item.membershipId,
        name: item.name ?? "",
        initials: item.initials,
        createdAt: item.createdAt,
        frequency: item.frequency,
        fillCurrent: item.fillCurrent,
        fillThreshold: item.fillThreshold,
        fillPercent: fillPercent(item),
        fillColor: fillColor(item),
        fillUnit: loyaltyCard.value ? objectiveUnitLabel(loyaltyCard.value) : "produit"
    };
    historyOpening.value++;
    historyOpen.value = true;
};

const headers = computed((): Array<{ title: string; key: string; align?: 'start' | 'center' | 'end' }> => [
    {title: 'Patient', key: 'patient'},
    {title: 'Carte créée le', key: 'createdAt'},
    {title: isPointCard.value ? 'Points cumulés' : 'Produits achetés', key: 'products', align: 'center'},
    {title: "Fréquence d'achat", key: 'frequency', align: 'center'},
    {title: 'Récompenses', key: 'rewards', align: 'center'},
    {title: 'Carte active', key: 'fill'}
]);
</script>

<template>
  <div>
    <!-- fill-rate filter -->
    <div class="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-gray-light">
      <span class="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon name="mdi-filter-variant" :size="16" color="#64748b" />Taux de remplissage
      </span>
      <button
        v-for="chip in fillChips"
        :key="chip.id"
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
        :class="searchParams.fill === chip.id
          ? 'border-primary bg-primary-lightest text-primary'
          : 'border-slate-200 bg-white text-slate-500 hover:border-gray'"
        @click="setFill(chip.id)"
      >
        <span v-if="chip.color" class="w-2 h-2 rounded-sm" :style="{ background: chip.color }" />
        {{ chip.label }}
      </button>
    </div>

    <v-data-table-server
      :headers="headers"
      :items="dataTableConsumers"
      :page="searchParams.page"
      :items-per-page="Number(searchParams.itemsPerPage)"
      :loading="isLoading || isValidating"
      :disable-sort="true"
      hover
      item-value="customerId"
      :items-length="consumers?.meta.total ?? 0"
      loading-text="Chargement en cours..."
      no-data-text="Aucune carte ne correspond à ce filtre"
      :footer-props="{
        'items-per-page-text': 'Éléments par page',
        'page-text': '{0}-{1} sur {2}',
        'total-items': 'Total des éléments'
      }"
      @update:page="handlePageChange"
      @update:items-per-page="handleItemPerPageChange"
      @click:row="handleRowClick"
    >
      <template #[`item.patient`]="{ item }">
        <div class="flex items-center gap-3 py-1">
          <span
            class="w-9 h-9 rounded-full bg-primary-lightest text-primary flex items-center justify-center text-xs font-semibold shrink-0"
          >
            {{ item.initials }}
          </span>
          <div class="text-[13px] font-semibold text-gray-dark">
            {{ item.name }}
          </div>
        </div>
      </template>

      <template #[`item.rewards`]="{ item }">
        <span
          v-if="item.rewardsCount > 0"
          class="inline-block bg-[#d6f8ea] text-emerald-700 font-semibold text-xs px-2.5 py-0.5 rounded-full tabular-nums"
        >
          {{ item.rewardsCount }}
        </span>
        <span v-else class="text-slate-400 text-xs tabular-nums">0</span>
      </template>

      <template #[`item.fill`]="{ item }">
        <div v-if="item.fillThreshold > 0" class="flex items-center gap-2 min-w-[120px]">
          <div class="flex-1 h-2 bg-gray-light rounded-full overflow-hidden">
            <div
              class="h-full rounded-full"
              :style="{ width: `${fillPercent(item)}%`, background: fillColor(item) }"
            />
          </div>
          <span class="text-[11px] font-semibold tabular-nums text-gray-dark w-10 text-right">
            {{ item.fillCurrent }}/{{ item.fillThreshold }}
          </span>
        </div>
        <span v-else class="text-slate-400 text-xs">—</span>
      </template>
    </v-data-table-server>

    <LoyaltyCardMembershipHistoryDialog
      v-if="historySummary"
      :key="historyOpening"
      v-model:open="historyOpen"
      :summary="historySummary"
    />
  </div>
</template>
